import {
  el, clear, panel, empty, chip, button, input, select, field, toast, fmtPhone, ago, muted, drawer,
} from '../ui.js';
import { get, post, patch, ApiError, type Me } from '../api.js';
import { fmtHour } from '../../../../packages/core/src/compliance.js';
import { INBOUND_OUTCOME_LABEL, type InboundOutcome } from '../../../../packages/core/src/inbound.js';

/**
 * Inbound — calls that arrive.
 *
 * NAMED FOR WHAT IT IS, after the person who asked for it went looking for
 * "Inbound" in the navigation and could not find a section called "Desk". The
 * label somebody reaches for IS the right label; a cleverer one they have to
 * learn is a cost with no payer. "The desk" survives inside the code where it
 * means the staffed roster — `deskOpen`, `DeskHours` — because that is what
 * those actually describe.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS ITS OWN SECTION AND NOT A PANEL ON THE DIALLING SCREEN
 * ---------------------------------------------------------------------------
 * The dialling screen belongs to one agent working a list, and everything on it
 * is about the next name. This belongs to the agency: it is the answer to "is
 * anybody covering the telephone", and the person who needs that answer is
 * usually not the person on a call.
 *
 * They also fail in opposite directions. A dialling screen that shows nothing
 * means there is nothing to dial, which is fine. A desk that shows nothing
 * means either that nobody rang or that every call is being dropped on the
 * floor, and those look identical unless the screen says which — so it does,
 * loudly, at the top.
 *
 * ---------------------------------------------------------------------------
 * THE ONE THING THIS SCREEN IS CAREFUL ABOUT
 * ---------------------------------------------------------------------------
 * It does not tell anybody who is calling.
 *
 * Caller ID is trivially spoofed, a household shares one handset, and a number
 * that belonged to somebody last year belongs to somebody else now. Every other
 * CRM opens the matching record — income, dependants, health answers — the
 * moment a number matches. That hands a stranger everything they need before
 * hello.
 *
 * So a match is shown as a SUGGESTION with the sentence that makes it one, and
 * the record stays shut until an agent presses "yes, this is them". What is
 * shown before that is a first name and a town, which is what you need to ask
 * "am I speaking to Maria?" — and nothing that would help somebody answer that
 * question falsely.
 */

interface DeskCall {
  id: string;
  fromE164: string | null;
  toE164: string;
  matchKind: 'none' | 'one' | 'many' | 'withheld';
  matchedLeadId: string | null;
  confirmedLeadId: string | null;
  state: 'ringing' | 'offering' | 'connected' | 'voicemail' | 'completed' | 'abandoned' | 'failed';
  routingBecause: string | null;
  answeredBy: string | null;
  createdAt: string;
  voicemailRef: string | null;
  voicemailSeconds: number | null;
  voicemailHeardAt: string | null;
  dncRequestedAt: string | null;
  outcome: string | null;
  suggestion: {
    id?: string; firstName?: string; lastName?: string; city?: string | null;
    state?: string | null; status?: string; suppressed?: boolean;
  } | null;
}

interface DeskNumber {
  id: string; e164: string; label: string | null; greeting: string | null;
  ringSeconds: number; active: boolean;
  /** Which advertisement this number is. */
  sourceId: string | null;
  /** Who its calls ring first. */
  routeToUserId: string | null;
  /** Or which team they belong to — the case that scales. */
  routeToTeamId: string | null;
}

interface DeskTeam { id: string; name: string; archivedAt: string | null }

interface Source { id: string; name: string; archivedAt: string | null }

/** The outbound record inbound is earned on. Null when there is none yet. */
interface Record_ {
  dials: number;
  contacts: number;
  contactRate: number | null;
  casesOpened: number;
  placed: number;
  annualPremiumCents: number;
  finding: 'too_early' | 'fine' | 'dials' | 'reach' | 'close';
  note: string | null;
}

interface RotaEntry {
  id: string; name: string; role: string;
  onRota: boolean; reachable: boolean; available: boolean;
  record: Record_ | null;
  mayGrant: boolean;
}

interface TeamRollUp {
  teamId: string | null;
  name: string;
  onDesk: number;
  onRota: number;
  live: number;
  unheard: number;
}

interface Board {
  live: DeskCall[];
  unheard: DeskCall[];
  open: boolean;
  hours: { tz: string; open: number; close: number; days: number[] };
  onDesk: Array<{ id: string; name: string; reachable: boolean }>;
  seeing: 'floor' | 'teams' | 'own';
  teams: TeamRollUp[];
  floor: { onDesk: number; onRota: number };
}

const DAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const STATE_LABEL: Record<DeskCall['state'], string> = {
  ringing: 'Ringing',
  offering: 'Offering to the desk',
  connected: 'On the line',
  voicemail: 'Leaving a message',
  completed: 'Finished',
  abandoned: 'Hung up before anybody answered',
  failed: 'Could not be connected',
};

export function inboundView(m: Me, go: (p: string) => void): HTMLElement {
  const host = el('div');
  const stage = el('div');
  let timer: number | null = null;

  host.append(el('div', { class: 'vhead' },
    el('span', { class: 'kicker', text: 'Inbound' }),
    el('h1', { text: 'Calls that come to us' }),
    el('p', {
      text: 'The number you dial out from is the number people ring back. This is where '
        + 'those calls land — and where somebody asking to be taken off the list is '
        + 'recorded the moment they ask.',
    })));
  host.append(stage);

  const load = async (): Promise<void> => {
    try {
      const [board, reg, sources, rota, deskTeams] = await Promise.all([
        get<Board>('/v1/inbound'),
        get<{ numbers: DeskNumber[]; answering: boolean; provider: string | null }>('/v1/inbound/numbers'),
        // Both are for the number registry: which advertisement a number is,
        // and who its calls go to. Failing softly because neither should be
        // able to keep a live call off the screen.
        get<Source[]>('/v1/sources').catch(() => [] as Source[]),
        /*
         * The routing picker is sourced from the ROTA, not from every member —
         * pointing a number at somebody off it is refused by the API, and an
         * option that always errors is worse than no option. A `/v1/users`
         * fetch lived here after that change and its result was never read:
         * that route needs `user.manage`, so for an agent on the rota it was a
         * permission denial every ten seconds, per open tab, for nothing.
         */
        get<RotaEntry[]>('/v1/inbound/rota').catch(() => [] as RotaEntry[]),
        /*
         * For the routing picker. Failing softly for the same reason the
         * sources fetch does: a team list nobody can read must not be able to
         * keep a live call off the screen.
         */
        get<DeskTeam[]>('/v1/teams').catch(() => [] as DeskTeam[]),
      ]);
      clear(stage);
      draw(stage, board, reg, sources, rota, deskTeams, m, go, load);
    } catch (e) {
      clear(stage);
      /*
       * Somebody off the rota lands here, and the refusal already says what to
       * do about it — so it is shown as written rather than replaced with
       * "could not read the desk", which reads as a broken screen and sends
       * them to ask why instead of to ask for access.
       */
      const msg = e instanceof ApiError ? e.message : 'Could not read the inbound line.';
      stage.append(el('div', { class: 'warnbox' },
        el('b', null, msg),
        e instanceof ApiError && e.code === 'forbidden'
          ? el('p', { text: 'Everything else in Cellvora still works \u2014 this section is the only one you are not on.' })
          : null));

      /*
       * ----------------------------------------------------------------------
       * AND THE ROTA PANEL ANYWAY, IF THEY CAN READ IT.
       * ----------------------------------------------------------------------
       * This screen locked an agency out of its own desk on the first day.
       * Everybody defaults off the rota, owners included; the board refused
       * them; the `Promise.all` above took the rota fetch down with it; and
       * the refusal they were left reading told them an owner could add them
       * under "Who takes these calls" — the panel that would have been on the
       * screen they had just been refused.
       *
       * The gate itself is fixed in `InboundService.board`. This is the second
       * half, and it is the half that matters longer: A SCREEN MUST NEVER HIDE
       * THE CONTROL THAT UNGATES IT. Whatever a future permission change does
       * to the board, the way back in stays reachable.
       */
      try {
        const rota = await get<RotaEntry[]>('/v1/inbound/rota');
        if (rota.length > 0) stage.append(rotaPanel(rota, m, load));
      } catch {
        /*
         * Genuinely nothing to offer. An agent who is off the rota and leads
         * nobody cannot grant anything to anyone, and the refusal above
         * already tells them who can.
         */
      }
    }
  };

  void load();
  /*
   * A live call is a person waiting, so the board refreshes on its own. Ten
   * seconds rather than one: this is a screen somebody glances at, and polling
   * a whole board every second to move a timestamp is a cost with no reader.
   */
  timer = setInterval(() => { void load(); }, 10_000) as unknown as number;
  host.addEventListener('cellvora:teardown', () => { if (timer) clearInterval(timer); });

  return host;
}

function draw(
  host: HTMLElement, b: Board,
  reg: { numbers: DeskNumber[]; answering: boolean; provider: string | null },
  sources: Source[], rota: RotaEntry[], deskTeams: DeskTeam[],
  m: Me, go: (p: string) => void, reload: () => Promise<void>,
): void {
  /* ------------------------------------------------ is anybody home --- */

  /*
   * THE FIRST THING ON THE SCREEN, because it is the question that makes every
   * other number on it meaningful. An empty board when the desk is unreachable
   * means calls are being dropped; an empty board when it is covered means a
   * quiet morning. Identical to look at, opposite in what they ask of you.
   */
  const noNumbers = reg.numbers.length === 0;
  const noProvider = !reg.answering;
  if (noNumbers || noProvider) {
    host.append(el('div', { class: 'warnbox' },
      el('b', null, noNumbers
        ? 'No numbers are registered, so nothing can reach this desk.'
        : 'No telephony provider is configured, so nothing rings here.'),
      el('p', {
        text: noNumbers
          ? 'Every number you dial out from is a number somebody can dial back. Until one is '
            + 'registered below, those calls reach nothing at all — and you never find out they happened.'
          : 'Numbers can be registered and calls will be recorded, but no telephone will ring '
            + 'until the provider credentials are set in the environment.',
      })));
  }

  const hours = `${DAY[b.hours.days[0] ?? 1]}–${DAY[b.hours.days[b.hours.days.length - 1] ?? 5]}, `
    + `${fmtHour(b.hours.open)}–${fmtHour(b.hours.close)}`;
  const cover = el('div', { style: 'display:flex;gap:10px;align-items:center;flex-wrap:wrap' },
    b.open ? chip('desk open', 'ok') : chip('desk closed', 'crit'),
    muted(hours),
    b.onDesk.length > 0
      ? el('span', { text: `${b.onDesk.length} at the desk: ${b.onDesk.map((u) => u.name).join(', ')}` })
      : el('b', { text: 'Nobody is at the desk — every call goes to voicemail.' }));

  const unreachable = b.onDesk.filter((u) => !u.reachable);
  if (unreachable.length > 0) {
    /*
     * Available with no telephone number on their record. They believe they are
     * covering the desk and no call can reach them, which is the most expensive
     * of the three ways a desk can be empty because it is the one nobody knows
     * about.
     */
    cover.append(el('div', { class: 'warnbox', style: 'width:100%;margin-top:8px' },
      el('b', null, `${unreachable.map((u) => u.name).join(', ')} `
        + `${unreachable.length === 1 ? 'is' : 'are'} at the desk with no telephone number on file.`),
      el('p', { text: 'A call cannot reach them. Add a number on their profile, or they are not really there.' })));
  }
  host.append(panel('Cover', cover));

  /* ------------------------------------------------------- roll-up --- */

  /*
   * "You have five active dialers."
   *
   * ------------------------------------------------------------------------
   * THE FIGURE THE PERSON ABOVE ACTUALLY LOOKS AT
   * ------------------------------------------------------------------------
   * An owner does not read a list of calls. They read whether the floor is
   * covered, team by team, and they go down one level when a row looks wrong.
   * That is the whole shape of this strip: one row per team, the count of
   * people who could pick up a telephone in the next ten seconds, and what
   * that team has live and unanswered.
   *
   * ON THE DESK is stricter than "logged in": on the rota, marked available,
   * seen in the last five minutes, with a telephone number on file. Anything
   * looser reports a staffed desk to somebody whose customers are hearing
   * voicemail, which is the one mistake this screen exists to prevent.
   *
   * ROWS DO NOT SUM TO THE FLOOR and nothing here pretends they do. Somebody
   * on two teams can answer for both, so both rows count them; the floor line
   * is counted from people instead. Printing a total under these columns would
   * be a made-up number.
   */
  if (b.seeing !== 'own' && b.teams.length > 0) {
    const live = b.teams.filter((t) => t.teamId !== null || t.live > 0 || t.unheard > 0 || t.onRota > 0);
    if (live.length > 0) {
      const rows = el('div');
      for (const t of live) {
        rows.append(el('div', {
          class: 'rowcard',
          style: 'display:flex;gap:12px;align-items:baseline;flex-wrap:wrap',
        },
        el('b', { text: t.name }),
        t.onDesk > 0
          ? chip(`${t.onDesk} on the desk`, 'ok')
          : t.onRota > 0
            ? chip(`${t.onRota} on the rota, none at a desk`, 'crit')
            : muted('nobody on the rota'),
        t.live > 0 ? chip(`${t.live} live`) : null,
        t.unheard > 0 ? chip(`${t.unheard} unheard`, 'crit') : null));
      }
      host.append(panel(
        b.seeing === 'floor' ? 'The floor' : 'Your teams',
        rows,
        el('p', {
          class: 'fine',
          text: `${b.floor.onDesk} of ${b.floor.onRota} on the inbound rota can take a call right `
            + 'now \u2014 on the rota, at their desk, seen in the last five minutes, with a '
            + 'telephone number on file. Somebody on two teams is counted on both rows, so the '
            + 'rows add up to more than the floor.',
        })));
    }
  }

  /* ------------------------------------------------------ live now --- */

  if (b.live.length === 0) {
    host.append(panel('Nothing live', empty(
      'No calls in progress',
      b.open
        ? 'When somebody rings one of your numbers it appears here, with what we know about the number and nothing more.'
        : 'The desk is closed, so anybody ringing now is leaving a message.',
    )));
  } else {
    const rows = el('div');
    for (const call of b.live) rows.append(liveCard(call, m, go, reload));
    host.append(panel(`${b.live.length} live`, rows));
  }

  /* ---------------------------------------------------- unheard --- */

  if (b.unheard.length > 0) {
    const rows = el('div');
    for (const call of b.unheard) rows.append(messageCard(call, go, reload));
    host.append(panel(`${b.unheard.length} message${b.unheard.length === 1 ? '' : 's'} nobody has heard`, rows,
      muted('A message stays here until somebody marks it heard. A list that only grows is a list nobody opens twice.')));
  }

  /* ---------------------------------------------------- numbers --- */

  host.append(numbersPanel(reg, sources, rota.filter((r) => r.onRota), deskTeams, m, reload));
  host.append(rotaPanel(rota, m, reload));
}

function liveCard(
  call: DeskCall, m: Me, go: (p: string) => void, reload: () => Promise<void>,
): HTMLElement {
  const head = el('div', { style: 'display:flex;gap:10px;align-items:baseline;flex-wrap:wrap' },
    el('b', { text: fmtPhone(call.fromE164) || 'Number withheld' }),
    chip(STATE_LABEL[call.state], call.state === 'connected' ? 'ok' : ''),
    muted(`on ${fmtPhone(call.toE164)} · ${ago(call.createdAt)}`));

  const body = el('div', { style: 'margin-top:8px' });

  /*
   * THE SUGGESTION, SAID AS A SUGGESTION.
   *
   * The sentence matters as much as the name. "This number is on file for one
   * record — confirm who you are speaking to before discussing it" is an
   * instruction an agent can follow. A name on its own, with the record open
   * beside it, is an invitation to start talking about somebody's coverage to
   * whoever happens to be holding their telephone.
   */
  if (call.confirmedLeadId) {
    body.append(el('div', null,
      chip('confirmed', 'ok'),
      el('a', {
        href: `#/leads/${call.confirmedLeadId}`,
        onclick: (e: Event) => { e.preventDefault(); go(`/leads/${call.confirmedLeadId}`); },
        style: 'margin-left:8px',
      }, 'Open the record')));
  } else if (call.matchKind === 'one' && call.suggestion) {
    const s = call.suggestion;
    const who = `${s.firstName ?? ''} ${s.lastName ?? ''}`.trim() || 'somebody';
    const where = [s.city, s.state].filter(Boolean).join(', ');
    body.append(el('div', { class: 'note' },
      el('b', null, `This number is on file for ${who}${where ? ` in ${where}` : ''}.`),
      el('p', { text: 'Caller ID can be faked and households share a telephone. Ask who you are speaking to before you open anything.' }),
      s.suppressed
        ? el('p', null, chip('this number is on your do-not-call list', 'crit'),
          ' — they may be ringing about exactly that.')
        : null,
      button('Yes, this is them', 'sm pri', () => {
        void (async () => {
          try {
            await post(`/v1/inbound/calls/${call.id}/confirm`, { leadId: call.matchedLeadId });
            toast('Attached to the record.', 'ok');
            await reload();
          } catch (e) {
            toast(e instanceof ApiError ? e.message : 'Could not attach the call.', 'crit');
          }
        })();
      })));
  } else if (call.matchKind === 'many') {
    body.append(el('div', { class: 'note' },
      el('b', null, 'This number is on file for more than one record.'),
      el('p', { text: 'Ask who is calling, then attach the call from their record. Opening one of them first is a coin toss.' })));
  } else if (call.matchKind === 'withheld') {
    body.append(el('div', { class: 'note' },
      el('b', null, 'The caller withheld their number.'),
      el('p', { text: 'Nothing to match on. Ask for a number if this turns into business.' })));
  } else {
    /*
     * THE ORDINARY CASE ON AN ADVERTISING LINE, AND IT USED TO BE A DEAD END.
     *
     * Somebody who saw an advert and rang the number on it has never been in
     * this system. Telling the agent "not on any record here" and sending them
     * to Leads means the most valuable call of the week gets written on a
     * biro-and-paper pad while the person is still talking.
     */
    body.append(el('div', { class: 'note' },
      el('b', null, 'A new enquiry \u2014 this number is on no record here.'),
      el('p', { text: 'Take their details while you have them. It files them against the advert they rang, and records the permission that lets you call them back.' }),
      button('Take their details', 'sm pri', () => void captureDrawer(call, reload))));
  }

  if (call.routingBecause) body.append(muted(call.routingBecause));

  body.append(actions(call, reload));
  return el('div', { class: 'rowcard' }, head, body);
}

function messageCard(call: DeskCall, go: (p: string) => void, reload: () => Promise<void>): HTMLElement {
  const secs = call.voicemailSeconds;
  return el('div', { class: 'rowcard' },
    el('div', { style: 'display:flex;gap:10px;align-items:baseline;flex-wrap:wrap' },
      el('b', { text: fmtPhone(call.fromE164) || 'Number withheld' }),
      muted(`${secs ? `${secs}s · ` : ''}${ago(call.createdAt)}`),
      call.routingBecause ? muted(call.routingBecause) : null),
    el('div', { style: 'margin-top:8px;display:flex;gap:8px;flex-wrap:wrap' },
      button('Mark heard', 'sm', () => {
        void (async () => {
          try {
            await post(`/v1/inbound/calls/${call.id}/heard`, {});
            await reload();
          } catch (e) {
            toast(e instanceof ApiError ? e.message : 'Could not mark it heard.', 'crit');
          }
        })();
      }),
      dncButton(call, reload)));
}

function actions(call: DeskCall, reload: () => Promise<void>): HTMLElement {
  const wrap = el('div', { style: 'margin-top:10px;display:flex;gap:8px;flex-wrap:wrap' });
  wrap.append(dncButton(call, reload));

  const outcomes: InboundOutcome[] = ['spoke', 'left_message', 'wrong_number', 'not_for_us'];
  for (const o of outcomes) {
    wrap.append(button(INBOUND_OUTCOME_LABEL[o], 'sm', () => {
      void (async () => {
        try {
          await post(`/v1/inbound/calls/${call.id}/outcome`, { outcome: o });
          toast('Noted.', 'ok');
          await reload();
        } catch (e) {
          toast(e instanceof ApiError ? e.message : 'Could not record that.', 'crit');
        }
      })();
    }));
  }
  return wrap;
}

/**
 * The button this whole section is built around.
 *
 * Deliberately the most prominent thing on a call card, and deliberately one
 * press. A removal request has a statutory clock that starts when the words are
 * said — not when somebody gets round to processing it — and every extra step
 * between hearing it and recording it is a chance for it to be lost on a busy
 * afternoon.
 *
 * It does NOT require a matched record, because the obligation attaches to the
 * NUMBER. Most numbers that ring a desk match nothing at all.
 */
function dncButton(call: DeskCall, reload: () => Promise<void>): HTMLElement {
  if (call.dncRequestedAt) return chip('taken off the list', 'ok');
  return button('Take them off the list', 'sm crit', () => {
    const note = input({ placeholder: 'Their words, if you caught them (optional)' });
    const close = drawer('Take this number off the list', el('div', null,
      el('p', {
        text: 'This adds the number they are calling from to your do-not-call list — the same '
          + 'one the dialler checks before every call — and suppresses every record carrying it.',
      }),
      el('p', {
        text: 'Their own words are worth recording. If it is ever questioned, the burden is on '
          + 'us to show what was asked for.',
      }),
      field('What they said', note),
      el('div', { style: 'display:flex;gap:8px;margin-top:12px' },
        button('Take them off the list', 'pri', () => {
          void (async () => {
            try {
              const r = await post<{ suppressedLeads: number }>(
                `/v1/inbound/calls/${call.id}/do-not-call`, { note: note.value.trim() || undefined },
              );
              close();
              toast(r.suppressedLeads > 0
                ? `Done — ${r.suppressedLeads} record${r.suppressedLeads === 1 ? '' : 's'} suppressed.`
                : 'Done — the number is on the list.', 'ok');
              await reload();
            } catch (e) {
              toast(e instanceof ApiError ? e.message : 'Could not record that.', 'crit');
            }
          })();
        }),
        button('Cancel', 'ghost', () => close()))));
  });
}

/*
 * The routing picker offers ONLY people on the rota, because pointing a number
 * at anybody else is refused by the API — and an option that always errors is
 * worse than no option. `members` was every active person; it is now the rota.
 */
function numbersPanel(
  reg: { numbers: DeskNumber[]; answering: boolean; provider: string | null },
  sources: Source[], members: RotaEntry[], teams: DeskTeam[],
  m: Me, reload: () => Promise<void>,
): HTMLElement {
  /*
   * `inbound.manage`, matching what the API actually asks for. This read
   * `user.manage`, which a MANAGER does not hold — so a manager opened the
   * registry read-only, with no "Add a number" form and no greeting button,
   * while both routes would have accepted them. A screen that hides a control
   * the server would allow is the same defect as one that shows a control the
   * server refuses, and it is harder to notice.
   */
  const canEdit = m.capabilities.includes('inbound.manage');
  const body = el('div');

  if (reg.numbers.length === 0) {
    body.append(empty(
      'No numbers registered',
      'Register the number you dial out from. It is the one your leads see, and the one they '
      + 'call back — most often minutes after a missed call.',
    ));
  } else {
    for (const n of reg.numbers) {
      const src = sources.find((x) => x.id === n.sourceId);
      const to = members.find((x) => x.id === n.routeToUserId);
      const toTeam = teams.find((x) => x.id === n.routeToTeamId);
      body.append(el('div', { class: 'rowcard' },
        el('div', { style: 'display:flex;gap:10px;align-items:baseline;flex-wrap:wrap' },
          el('b', { text: fmtPhone(n.e164) }),
          n.label ? muted(n.label) : null,
          n.active ? chip('answering', 'ok') : chip('switched off', 'crit'),
          muted(`rings for ${n.ringSeconds}s`)),
        /*
         * Both stated, including when they are absent. A number with no advert
         * behind it measures nothing, and a number pointed at nobody rings
         * whoever is nearest — neither is wrong, but an agency running ads
         * should be able to see which of its numbers are in that state without
         * opening anything.
         */
        el('div', { style: 'margin-top:6px;display:flex;gap:8px;flex-wrap:wrap' },
          src ? chip(`advert: ${src.name}`) : muted('no advert linked \u2014 spend on this number is not measured'),
          /*
           * Both routes shown, in the order `chooseRing` ranks them. A number
           * can carry a named person AND a team — they are different facts and
           * an agency has both — so this states which wins rather than hiding
           * one of them.
           */
          to ? chip(`goes to ${to.name}`, 'ok') : null,
          toTeam ? chip(to ? `then ${toTeam.name}` : `goes to ${toTeam.name}`, 'ok') : null,
          !to && !toTeam ? muted('goes to whoever is at the desk') : null),
        canEdit
          ? el('div', { style: 'margin-top:8px;display:flex;gap:8px;flex-wrap:wrap' },
            button(n.active ? 'Switch off' : 'Switch on', 'sm', () => {
              void (async () => {
                try {
                  await patch(`/v1/inbound/numbers/${n.id}`, { active: !n.active });
                  await reload();
                } catch (e) {
                  toast(e instanceof ApiError ? e.message : 'Could not change it.', 'crit');
                }
              })();
            }),
            button('Greeting', 'sm ghost', () => void editGreeting(n, reload)))
          : null));
    }
  }

  if (canEdit) {
    const num = input({ placeholder: '(313) 555-0142' });
    const label = input({ placeholder: 'What this number is for (optional)' });
    const live = sources.filter((x) => !x.archivedAt);
    const srcPick = select(
      [['', 'No advert \u2014 do not measure this number'],
        ...live.map((x) => [x.id, x.name] as [string, string])],
    );
    const toPick = select(
      [['', 'Whoever is at the desk'],
        ...members.map((u) => [u.id, u.name] as [string, string])],
    );
    const teamPick = select(
      [['', 'No team \u2014 the whole rota'],
        ...teams.filter((t) => !t.archivedAt).map((t) => [t.id, t.name] as [string, string])],
    );
    body.append(el('div', { style: 'margin-top:14px' },
      field('Add a number you answer', num,
        'The same number you dial out from, or the one on an advert. A number can only belong to one agency.'),
      field('Label', label),
      field('Which advert is this?', srcPick,
        'Links its calls to a lead source, so cost per call and cost per sale are measured by '
        + 'the same reporting that measures a bought list.'),
      field('Who do these calls go to?', toPick,
        'Rings them first, ahead of anybody else at the desk \u2014 and falls back to the desk when '
        + 'they have gone home. Whoever pays for the advert should get its calls.'),
      field('Or which team?', teamPick,
        'The case that scales: a team runs a campaign and its calls should reach whoever on that '
        + 'team is at a telephone. A named person above beats the team; the team beats whoever '
        + 'happens to own the matching record. A team with nobody on the inbound rota is refused, '
        + 'because those calls would reach nobody.'),
      button('Register it', 'sm pri', () => {
        void (async () => {
          try {
            await post('/v1/inbound/numbers', {
              e164: num.value.trim(),
              label: label.value.trim() || undefined,
              sourceId: srcPick.value || undefined,
              routeToUserId: toPick.value || undefined,
              routeToTeamId: teamPick.value || undefined,
            });
            toast('Registered.', 'ok');
            await reload();
          } catch (e) {
            toast(e instanceof ApiError ? e.message : 'Could not register that number.', 'crit');
          }
        })();
      })));
  }

  return panel('Numbers you answer', body);
}

/**
 * The greeting, with the sentence the agency cannot delete shown to them.
 *
 * It is appended by the platform because it is what makes recording the message
 * lawful — in several states recording without it is a criminal matter rather
 * than a civil one. An agency that can SEE it will not be surprised by it, and
 * showing it here is cheaper than explaining it later.
 */
async function editGreeting(n: DeskNumber, reload: () => Promise<void>): Promise<void> {
  let preview = { text: '', custom: '', truncated: false };
  try {
    preview = await get<{ text: string; custom: string; truncated: boolean }>(
      `/v1/inbound/numbers/${n.id}/greeting`,
    );
  } catch { /* fall through with an empty preview rather than blocking the drawer */ }

  const ta = el('textarea', {
    rows: '3', class: 'ta',
    placeholder: 'Thank you for calling. There is nobody available to take your call.',
  }) as HTMLTextAreaElement;
  ta.value = n.greeting ?? '';

  const spoken = el('div', { class: 'note' },
    el('b', null, 'What the caller actually hears'),
    el('p', { text: preview.text || '—' }));

  const close = drawer(`Greeting for ${fmtPhone(n.e164)}`, el('div', null,
    el('p', {
      text: 'Played when nobody picks up, before the message is recorded.',
    }),
    field('Your words', ta),
    spoken,
    el('p', {
      class: 'fine',
      text: 'The line about the message being recorded is added automatically and cannot be '
        + 'removed. In several states a caller has to be told before a recording starts, and '
        + 'their choosing to speak after hearing it is what makes the recording lawful.',
    }),
    el('div', { style: 'display:flex;gap:8px;margin-top:12px' },
      button('Save', 'pri', () => {
        void (async () => {
          try {
            await patch(`/v1/inbound/numbers/${n.id}`, { greeting: ta.value.trim() });
            close();
            toast('Greeting saved.', 'ok');
            await reload();
          } catch (e) {
            toast(e instanceof ApiError ? e.message : 'Could not save it.', 'crit');
          }
        })();
      }),
      button('Cancel', 'ghost', () => close()))));
}


/**
 * A caller who is on no list, written down while they are still on the line.
 *
 * THE CONSENT BOX IS THE POINT, not the name fields. Somebody who telephoned
 * US is the strongest consent position this product has — the do-not-call
 * rules govern calls made TO a consumer, and this person dialled. Without the
 * record, an enquiry who does not buy today cannot lawfully be rung tomorrow,
 * and the best lead of the week is gone.
 *
 * So it asks for their words rather than a tick. "They said yes" proves
 * nothing a year later when somebody asks what was agreed, which is the same
 * rule the consent screen already enforces and is reached through the same
 * method so the two cannot drift.
 */
async function captureDrawer(call: DeskCall, reload: () => Promise<void>): Promise<void> {
  const first = input({ placeholder: 'First name' });
  const last = input({ placeholder: 'Surname' });
  const state = input({ placeholder: 'State (MI, FL\u2026)', maxlength: '2' });
  const said = el('textarea', {
    rows: '2', class: 'ta',
    placeholder: 'What they said about being contacted \u2014 their words if you have them',
  }) as HTMLTextAreaElement;
  const none = input({ type: 'checkbox' });

  const close = drawer(`New enquiry from ${fmtPhone(call.fromE164)}`, el('div', null,
    el('p', { text: 'Filed against the number they rang, so the advert that produced them is on the record.' }),
    field('First name', first),
    field('Surname', last),
    field('State', state, 'Decides the calling window and which licence is needed.'),
    field('What they said about being contacted', said,
      'This is what lets you ring them back. Write what they actually said.'),
    el('label', { class: 'chk' }, none,
      el('span', { text: ' They did not agree to be contacted' })),
    el('p', {
      class: 'fine',
      text: 'Somebody who rang you is the strongest permission there is. Without it recorded, '
        + 'an enquiry who does not buy today cannot be called tomorrow.',
    }),
    el('div', { style: 'display:flex;gap:8px;margin-top:12px' },
      button('Add them', 'pri', () => {
        void (async () => {
          try {
            const r = await post<{ leadId: string; consented: boolean }>(
              `/v1/inbound/calls/${call.id}/capture`,
              {
                firstName: first.value.trim() || undefined,
                lastName: last.value.trim() || undefined,
                state: state.value.trim().toUpperCase() || undefined,
                languageText: said.value.trim() || undefined,
                noConsent: none.checked || undefined,
              },
            );
            close();
            toast(r.consented ? 'Added, with their permission on file.' : 'Added.', 'ok');
            await reload();
          } catch (e) {
            toast(e instanceof ApiError ? e.message : 'Could not add them.', 'crit');
          }
        })();
      }),
      button('Cancel', 'ghost', () => close()))));
}

/**
 * Who takes these calls.
 *
 * ---------------------------------------------------------------------------
 * THE SCREEN THAT MAKES THE ACCESS RULE READABLE
 * ---------------------------------------------------------------------------
 * A permission nobody can see is a permission nobody manages. Everybody active
 * is listed, on the rota and off, because the question an owner is answering is
 * "who should be taking these" — and a list of only the people already on it
 * cannot answer that.
 *
 * Being on the rota and being reachable are shown together on purpose. On the
 * rota with no telephone number on file is the quietest failure in this whole
 * subsystem: they believe they are covering the line, the board counts them,
 * and nothing rings.
 */
function rotaPanel(rota: RotaEntry[], m: Me, reload: () => Promise<void>): HTMLElement {
  /*
   * `mayGrant` comes from the API, per person, rather than being re-derived
   * here from a capability. It has to: the rule is no longer "does this person
   * hold `user.manage`" but "is this person an owner, OR a team leader looking
   * at somebody on their own team", and a screen that guessed at that would be
   * a second copy of a permission rule \u2014 which is how one of them goes stale in
   * the direction of showing a control the server refuses.
   */
  const canGrantAny = rota.some((r) => r.mayGrant);
  const body = el('div');

  const on = rota.filter((r) => r.onRota);
  if (on.length === 0) {
    body.append(el('div', { class: 'warnbox' },
      el('b', null, 'Nobody is on the inbound rota.'),
      el('p', {
        text: 'Every call that comes in goes straight to voicemail. That is the safe failure '
          + '— a line that reaches nobody beats one that reaches whoever happened to mark '
          + 'themselves available — but it is not what you want on a day you are paying for ads.',
      })));
  }

  for (const r of rota) {
    const flags = el('div', { style: 'display:flex;gap:6px;flex-wrap:wrap;align-items:center' },
      el('b', { text: r.name }),
      muted(r.role),
      r.onRota ? chip('takes inbound', 'ok') : muted('not on the rota'),
      r.onRota && !r.reachable
        ? chip('no telephone number — cannot be rung', 'crit')
        : null,
      r.onRota && r.reachable && r.available ? chip('at their desk') : null);

    body.append(el('div', { class: 'rowcard' }, flags,
      recordLine(r),
      r.mayGrant
        ? el('div', { style: 'margin-top:8px' },
          button(r.onRota ? 'Take off the rota' : 'Put on the rota', r.onRota ? 'sm' : 'sm pri',
            () => {
              void (async () => {
                try {
                  const res = await post<{ caution: string | null }>(
                    `/v1/inbound/rota/${r.id}`, { on: !r.onRota },
                  );
                  /*
                   * The caution is shown AFTER the change, not instead of it.
                   * The server does not refuse a grant to somebody with a thin
                   * record and neither does this: an agency can have perfectly
                   * good reasons, and software that blocked would be overruling
                   * an owner about their own floor. It says the thing out loud.
                   */
                  if (res.caution) toast(res.caution, 'crit');
                  else toast(r.onRota ? `${r.name} taken off.` : `${r.name} added.`, 'ok');
                  await reload();
                } catch (e) {
                  toast(e instanceof ApiError ? e.message : 'Could not change that.', 'crit');
                }
              })();
            }))
        : null));
  }

  if (!canGrantAny) {
    body.append(muted('An owner or administrator decides who takes inbound calls, and a team '
      + 'leader decides it for their own team.'));
  }

  return panel('Who takes these calls', body,
    el('p', {
      class: 'fine',
      text: 'Inbound is earned. The last month of outbound work sits beside each name, because '
        + 'that is what the decision is actually made on \u2014 and the figures at the moment you '
        + 'decide are written into the audit trail with it. Nothing here grants access on its '
        + 'own: a number that flips a permission is a number people start working instead of '
        + 'working the telephone.',
    }),
    el('p', {
      class: 'fine',
      text: 'Being on the rota is granted here. Marking yourself available is something anybody '
        + 'can do for themselves and does not put you on it — those are two different '
        + 'questions, and only this one decides whether a customer can reach you.',
    }));
}

/**
 * What somebody has done on the telephone, beside the switch that decides
 * whether they get the expensive leads.
 *
 * ---------------------------------------------------------------------------
 * "YOU HAVE BEEN DOING GOOD ON THE PHONE WITH THE OUTBOUNDS."
 * ---------------------------------------------------------------------------
 * Jake's rule, in the numbers it is actually made on. The same figures the
 * scoreboard shows, from the same service \u2014 not a second count, which would
 * eventually disagree, and the copy on the screen where access is granted is
 * the worse of the two to have wrong.
 *
 * NULL IS THE SIGNAL, NOT A GAP. No row means no dials and no cases in the
 * window: somebody who has not worked a queue, which is exactly the person
 * Jake described not giving inbounds to.
 */
function recordLine(r: RotaEntry): HTMLElement {
  const rec = r.record;
  if (!rec) {
    return el('div', { style: 'margin-top:6px' },
      muted('No outbound work in the last month.'));
  }
  const bits: string[] = [
    `${rec.dials} dial${rec.dials === 1 ? '' : 's'}`,
    `${rec.contacts} conversation${rec.contacts === 1 ? '' : 's'}`,
  ];
  if (rec.contactRate !== null) bits.push(`${Math.round(rec.contactRate * 100)}% reach`);
  if (rec.casesOpened > 0) bits.push(`${rec.casesOpened} case${rec.casesOpened === 1 ? '' : 's'}`);
  if (rec.placed > 0) {
    bits.push(`${rec.placed} placed`);
    bits.push(`$${Math.round(rec.annualPremiumCents / 100).toLocaleString()} annual premium`);
  }
  const row = el('div', {
    style: 'margin-top:6px;display:flex;gap:8px;flex-wrap:wrap;align-items:center',
  }, muted(bits.join(' \u00b7 ')));
  if (rec.finding === 'too_early') row.append(chip('too early to say', 'crit'));
  else if (rec.finding !== 'fine') row.append(chip(rec.note ?? rec.finding, 'crit'));
  return row;
}
